/**
 * PLAT-10913 / PLAT-10992 — the AI text control's UI.
 *
 * PRESENTATION ONLY (epic decision E1 / story decision DS1): props in,
 * callbacks out. No store, no service, and no knowledge of which surface it
 * is mounted on, so the two adapters — the clip-preview quick-edit popover
 * (this ticket) and the `CompositionControl` switch (PLAT-10920) — share the
 * component without either deforming it.
 *
 * PLAT-10992 simplified the panel: the default view is just the result and one
 * action. Top to bottom — a compact "Options" stepper that cycles the AI
 * results into the row's text (PLAT-10916), the Regenerate action, and an
 * "Advanced" disclosure that folds the steering instructions and the base-text
 * scope out of sight until the creator wants them.
 *
 * The component owns no fetching: `onGenerate` goes out, `isGenerating` /
 * `error` come back in. PLAT-10915 wired the adapters to a real generation, so
 * Regenerate runs — and doubles as the Retry for a generation that failed when
 * the composition was added, the one thing that must be reachable without
 * reading the rest of the panel.
 */
import {
  trackGraphicsAiTextBaseTextChanged,
  trackGraphicsAiTextStaleShown,
} from '@src/analytics/EventTracker';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { DisclosureChevron } from '@src/components/transcript/translationEditorPrimitives';
import CreateIcon from '@src/svgs/CreateIcon';
import { convertTimeToMMSS } from '@src/utility/timeFormat';
import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { useAITextBaseText } from './useAITextBaseText';
import { useAITextStaleness } from './useAITextStaleness';

import type { AITextBaseTextScope } from '../types';
import type { GraphicsAITextIdentity } from '@src/analytics/EventTracker';
import type { AITextTranscriptElement } from './useAITextBaseText';
import type { TimelineMarker } from '@src/components/transcript/hooks/useTimelineMarkers';

export interface AITextGenerateRequest {
  baseTextScope: AITextBaseTextScope;
  /** The resolved words for that scope — what the model will read. */
  baseText: string;
  /** Free-text steer, empty when the user typed nothing. */
  instructions: string;
}

export interface AITextControlProps {
  /** Control label ("Hook"). */
  label: string;
  /** Base-text scope the control opens on. Defaults to `selection`. */
  defaultBaseTextScope?: AITextBaseTextScope;
  /**
   * Timeline marker for the graphic this control belongs to — the anchor /
   * time window `findOverlayRanges` maps the selection from (DS3).
   */
  overlay: TimelineMarker | null;
  /** The clip's transcript elements. */
  transcriptElements: ReadonlyArray<AITextTranscriptElement> | null | undefined;
  onGenerate: (request: AITextGenerateRequest) => void;
  isGenerating?: boolean;
  error?: string | null;
  /**
   * PLAT-10916 — every option the last generation returned, from the
   * provenance blob. Fewer than two means there is nothing to pick between and
   * the stepper doesn't render at all (DS5).
   */
  options?: ReadonlyArray<string>;
  /** Index into `options` last applied to the element. */
  selectedOption?: number;
  /**
   * PLAT-10917 DS2 — the steer the last generation ran with, from the
   * provenance blob. Seeds the input ONCE, on mount: after that the box is the
   * creator's, and re-seeding it from the blob when a run lands would fight
   * whatever they were in the middle of typing.
   *
   * The effect is that reopening the popover shows the nudge that produced the
   * text on the canvas, and appending to it builds on that nudge rather than
   * silently starting over.
   */
  defaultInstructions?: string;
  /**
   * PLAT-10918 — the fingerprint of the base text the last generation read,
   * from the provenance blob. Compared against the CURRENT base text to flag
   * "Transcript changed"; absent means the control never generated, which is
   * not stale (DS5).
   */
  sourceFingerprint?: string;
  /**
   * PLAT-10918 DS2 — the scope that run read. Separate from
   * `defaultBaseTextScope` (which the selector below owns and the creator can
   * flip) because the compare must re-resolve what was GENERATED from, not what
   * the panel currently shows.
   */
  generatedBaseTextScope?: AITextBaseTextScope;
  /** Omit (with a surface that wires no selection) to hide the stepper. */
  onSelectOption?: (index: number) => void;
  /** Disambiguates test ids + tooltip ids when several rows are open. */
  idSuffix?: string | number;
  /**
   * PLAT-10923 — analytics identity for the two events this component owns: the
   * base-text switch and the staleness indicator. Both are states no adapter
   * can observe — the scope is local state here and staleness is computed here —
   * so they fire from this component, which keeps them at one call site apiece
   * across every surface (DS4). Built by `AITextGeneration.analytics`, so it
   * stays props-in: the component still learns nothing about which surface it is
   * on beyond passing this through.
   *
   * Omitted by test harnesses and by surfaces that wire no generation; the two
   * events then don't fire, exactly as the rest of the panel goes inert.
   */
  analytics?: GraphicsAITextIdentity;
}

export const AITextControl: React.FC<AITextControlProps> = ({
  label,
  defaultBaseTextScope = 'selection',
  overlay,
  transcriptElements,
  onGenerate,
  isGenerating = false,
  error = null,
  options,
  selectedOption = 0,
  onSelectOption,
  defaultInstructions,
  sourceFingerprint,
  generatedBaseTextScope,
  idSuffix = 0,
  analytics,
}) => {
  // PLAT-10992 — Instructions and base-text scope live behind one collapsed
  // "Advanced" disclosure: they are the steer you reach for occasionally, not
  // the result you came to see. The default view is the Options stepper plus
  // Regenerate, nothing else.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [scope, setScope] = useState<AITextBaseTextScope>(defaultBaseTextScope);
  const [instructions, setInstructions] = useState(defaultInstructions ?? '');

  const baseText = useAITextBaseText({ scope, overlay, transcriptElements });
  /*
     PLAT-10918 — lives HERE, in the shared component, not in either adapter:
     staleness is implemented once and therefore appears on every AI text
     control on every surface. A control that could be stale in the clip-preview
     popover and silent in the Creator Studio panel would be worse than no
     indicator at all, because the creator would learn to trust its absence.
  */
  const { isStale } = useAITextStaleness({
    sourceFingerprint,
    generatedBaseTextScope,
    overlay,
    transcriptElements,
  });
  /*
     PLAT-10923 — the identity, read at fire time rather than depended on.
     `AITextGeneration.analytics` returns a fresh object per render (it resolves
     the template id off the live video), so listing it in the effect below
     would re-fire the staleness event on every render — the exact render
     counter DS5 exists to prevent.
  */
  const analyticsRef = useRef(analytics);
  analyticsRef.current = analytics;

  /*
     PLAT-10923 DS5 — once per transition INTO stale, not once per render. The
     indicator re-renders constantly (every keystroke in the instructions box
     re-renders this component), so a bare fire in the render body would make the
     event a render counter and the metric meaningless.

     Reset when the control leaves the stale state, because a Regenerate that
     clears it and a later transcript edit that brings it back is a genuine
     second occurrence — not the same one counted twice.
  */
  const staleTracked = useRef(false);
  useEffect(() => {
    if (!isStale) {
      staleTracked.current = false;
      return;
    }
    if (staleTracked.current) return;
    staleTracked.current = true;
    const identity = analyticsRef.current;
    if (!identity) return;
    trackGraphicsAiTextStaleShown({
      ...identity,
      // The scope the STALE generation read — the one the compare re-resolves —
      // not whatever the selector happens to show now.
      base_text: generatedBaseTextScope ?? scope,
    });
  }, [isStale, generatedBaseTextScope, scope]);

  const changeScope = (next: AITextBaseTextScope) => {
    // Re-picking the selected segment is not a change, and counting it would
    // inflate the only number this event reports.
    if (next === scope) return;
    setScope(next);
    const identity = analyticsRef.current;
    if (identity) {
      trackGraphicsAiTextBaseTextChanged({
        ...identity,
        from: scope,
        to: next,
      });
    }
  };

  const baseTextTooltipId = `ai-text-control-basetext-info-${idSuffix}`;
  const staleTooltipId = `ai-text-control-stale-${idSuffix}`;

  const runGeneration = () =>
    onGenerate({
      baseTextScope: scope,
      baseText: baseText.text,
      instructions: instructions.trim(),
    });

  // PLAT-10992 — the stepper cycles the applied option one at a time, writing
  // each into the row's text as it lands (via `onSelectOption`). Clamped at the
  // ends rather than wrapping, so the arrows read as a bounded list.
  const optionCount = options?.length ?? 0;
  const showStepper = optionCount >= 2 && !!onSelectOption;
  const atFirstOption = selectedOption <= 0;
  const atLastOption = selectedOption >= optionCount - 1;

  /*
     What the model will actually read, shown in full inside the open Advanced
     section. The clip scope summarizes rather than dumping the whole clip
     transcript (DS3) — it is routinely thousands of words, and the count plus
     the runtime is what tells the creator whether the scope is the one they
     meant. Selection quotes the words, because there are few enough to check.
  */
  const sourceSummary =
    scope === 'clip'
      ? baseText.wordCount === 0
        ? 'No transcript for this clip.'
        : `Everything said in this clip — ${baseText.wordCount} ${pluralize(
            'word',
            baseText.wordCount,
          )}, ${convertTimeToMMSS(baseText.durationSeconds)}.`
      : baseText.unresolved
        ? "This graphic doesn't cover any transcript words."
        : baseText.text;

  // A quiet summary on the collapsed Advanced row so the creator sees which
  // base text the model reads without expanding.
  const advancedSummary = scope === 'clip' ? 'Clip' : 'Selection';

  return (
    <Section data-testid={`ai-text-control-${idSuffix}`}>
      <Body id={`ai-text-control-body-${idSuffix}`}>
        {/*
            PLAT-10916 / PLAT-10992 — the alternatives, as a compact stepper
            rather than a dropdown. It cycles each option into the row's text as
            it lands, so the text field above is the single source of truth for
            what is applied. Absent before the first generation, and absent when
            a run yielded a single usable option — a stepper of one is not a
            stepper (DS5).
          */}
        {showStepper ? (
          <Stepper data-testid={`ai-text-options-${idSuffix}`}>
            <StepButton
              type="button"
              disabled={atFirstOption}
              aria-label="Previous option"
              onClick={() => onSelectOption?.(selectedOption - 1)}
              data-testid={`ai-text-options-prev-${idSuffix}`}
            >
              ‹
            </StepButton>
            <StepCount data-testid={`ai-text-options-count-${idSuffix}`}>
              {selectedOption + 1} of {optionCount}
            </StepCount>
            <StepButton
              type="button"
              disabled={atLastOption}
              aria-label="Next option"
              onClick={() => onSelectOption?.(selectedOption + 1)}
              data-testid={`ai-text-options-next-${idSuffix}`}
            >
              ›
            </StepButton>
          </Stepper>
        ) : null}

        {/*
            PLAT-10992 — the steer sits in the default view, directly above the
            action it feeds, so "say how, then run it" reads top to bottom. It is
            the core of the regenerate loop, not advanced configuration, so it is
            not folded away.
          */}
        <FieldLabel
          as="label"
          htmlFor={`ai-text-control-instructions-${idSuffix}`}
        >
          Instructions
          <FieldHint> (optional)</FieldHint>
        </FieldLabel>
        <Instructions
          id={`ai-text-control-instructions-${idSuffix}`}
          value={instructions}
          rows={2}
          placeholder="Shorter, punchier, make it a question…"
          onChange={e => setInstructions(e.target.value)}
          data-testid={`ai-text-control-instructions-${idSuffix}`}
        />

        {/*
            PLAT-10915 DS3 — no silent failure. The error and its retry are on
            screen the moment quick edit opens; the button below doubles as the
            retry rather than a second one.
          */}
        {error ? (
          <ErrorText
            role="alert"
            data-testid={`ai-text-control-error-${idSuffix}`}
          >
            {error}
          </ErrorText>
        ) : null}

        {/*
            PLAT-10918 — the staleness indicator, directly above the only remedy
            (DS4). A state, not a notification: no dismiss, no snooze, and
            nothing regenerates on its own — it clears when a re-run refreshes
            the fingerprint.
          */}
        {isStale ? (
          <>
            <StaleNotice
              data-tooltip-id={staleTooltipId}
              data-tooltip-content={STALE_INFO}
              data-testid={`ai-text-control-stale-${idSuffix}`}
              aria-label={STALE_INFO}
            >
              <StaleMark aria-hidden="true">!</StaleMark>
              Transcript changed
            </StaleNotice>
            <Tooltip
              id={staleTooltipId}
              style={INFO_TOOLTIP_STYLE}
              place="left"
            />
          </>
        ) : null}

        {/*
            PLAT-10992 — the AI action, marked with the same CreateIcon glyph
            the app's green "Generate" buttons use (ContentButton `generate`).
            A green OUTLINE, not a solid fill: the popover's own Save is a solid
            green a few centimetres below, and two solid greens read as two
            equal primaries — Save is the one that commits.
          */}
        <RegenerateButton
          type="button"
          disabled={isGenerating}
          onClick={() => runGeneration()}
          data-testid={`ai-text-control-regenerate-${idSuffix}`}
        >
          <CreateIcon width="14" height="14" strokeColor="currentColor" />
          {isGenerating ? 'Generating…' : error ? 'Retry' : 'Regenerate'}
        </RegenerateButton>

        <Divider />

        {/*
            PLAT-10992 — base-text scope, collapsed by default: which words the
            model reads is configuration you set once, not part of the
            read-then-regenerate loop above.
          */}
        <SectionHeaderRow>
          <SectionToggle
            type="button"
            aria-expanded={advancedOpen}
            aria-controls={`ai-text-control-advanced-${idSuffix}`}
            onClick={() => setAdvancedOpen(v => !v)}
            data-testid={`ai-text-control-advanced-toggle-${idSuffix}`}
          >
            <DisclosureChevron expanded={advancedOpen} size={9} />
            <HeaderLabel as="span">Advanced</HeaderLabel>
          </SectionToggle>
          <ToggleSummary>{advancedSummary}</ToggleSummary>
        </SectionHeaderRow>

        {advancedOpen ? (
          <AdvancedBody id={`ai-text-control-advanced-${idSuffix}`}>
            <BaseTextLabelRow>
              <FieldLabel as="span">Base text for AI generation</FieldLabel>
              <InfoMark
                data-tooltip-id={baseTextTooltipId}
                data-tooltip-content={BASE_TEXT_INFO}
                data-testid={`ai-text-control-basetext-info-${idSuffix}`}
                aria-label={BASE_TEXT_INFO}
              >
                i
              </InfoMark>
              <Tooltip
                id={baseTextTooltipId}
                style={INFO_TOOLTIP_STYLE}
                place="left"
              />
            </BaseTextLabelRow>
            <ScopeRow role="radiogroup" aria-label={`${label} base text`}>
              <ScopeButton
                type="button"
                role="radio"
                aria-checked={scope === 'clip'}
                data-selected={scope === 'clip' || undefined}
                onClick={() => changeScope('clip')}
                data-testid={`ai-text-control-scope-clip-${idSuffix}`}
              >
                Clip
              </ScopeButton>
              <ScopeButton
                type="button"
                role="radio"
                aria-checked={scope === 'selection'}
                data-selected={scope === 'selection' || undefined}
                onClick={() => changeScope('selection')}
                data-testid={`ai-text-control-scope-selection-${idSuffix}`}
              >
                Selection
              </ScopeButton>
            </ScopeRow>
            <Preview data-testid={`ai-text-control-source-text-${idSuffix}`}>
              {sourceSummary}
            </Preview>
          </AdvancedBody>
        ) : null}
      </Body>
    </Section>
  );
};

/* The info mark says what the section IS. What it currently resolves TO is the
   section's own content — stated in full in the base-text preview once Advanced
   is open — so the tooltip never has to carry a paragraph of transcript. */
const BASE_TEXT_INFO =
  'Scope of the text used for generation. Clip means all text, selection means only what was selected.';
/* Says what happened and what the remedy costs — the text is the creator's now,
   possibly hand-tuned, so the tooltip is explicit that re-running replaces it
   rather than implying the graphic is broken. */
const STALE_INFO =
  'This text was written from an older version of the transcript. Regenerating will replace it.';

function pluralize(word: string, n: number): string {
  return n === 1 ? word : `${word}s`;
}

/* The gap above separates the AI block from the hand-editable textarea it
   belongs to. It has to come from HERE rather than from either surface: the
   popover parents the two in a `FieldRow` (`gap: 4px`), the settings panel
   renders them as bare siblings with no gap at all, so a 2px margin left the
   panel's block all but touching its field. 10px keeps it visibly the field's
   block while still reading as its own thing — deliberately less than the
   ~15px `ControlFieldLabel` puts between unrelated panel blocks. */
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
`;

/* Green OUTLINE, not a solid fill. Green because docs/design-system.md §
   Color System puts Generate buttons under `--primary`. Outline because the
   popover's own Save is a solid green button a few centimetres below: two solid
   greens in one dialog read as two equal primary actions, and Save is the one
   that commits. The outline keeps Regenerate unmistakably the green action of
   THIS panel while staying subordinate to the dialog's. */
const RegenerateButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 1px;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  line-height: 1.4;
  padding: 5px 10px;
  border-radius: var(--radius-input);
  cursor: pointer;
  color: var(--primary);
  background: transparent;
  border: 1px solid var(--primary);
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  svg {
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    background: color-mix(in srgb, var(--primary) 14%, transparent);
    border-color: var(--primary-hover);
  }
  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 1px;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/* Hairline between the result-and-action block and the collapsed steering
   controls — two different jobs sharing one panel. */
const Divider = styled.hr`
  height: 0;
  margin: 3px 0 1px;
  border: none;
  border-top: 1px solid var(--border);
`;

/* PLAT-10992 — flat, not a bordered box. The control's contents sit directly
   in the popover flow beneath the Hook field so the Options stepper reads as
   part of that field rather than a second panel-in-a-panel. Structure comes
   from the divider + the Advanced row, not an outer border. */
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

/* The revealed Advanced contents get their own gap so the label/field pairs
   inside don't inherit the tighter Body rhythm. */
const AdvancedBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 1px;
`;

const FieldLabel = styled.div`
  font-weight: var(--font-weight-medium);
  font-size: 11px;
  line-height: 1.2;
  color: var(--popover-foreground);
  opacity: 0.65;
`;

const FieldHint = styled.span`
  font-weight: var(--font-weight-regular);
`;

/* PLAT-10992 — the alternatives stepper: bounded prev/next arrows flanking an
   "N of M" count that reads as a togglable list, not a passive counter.
   Centered beneath the text box because it PAGINATES that box (like carousel
   controls under an image); centering ties it to the content above and, with no
   label, reads unmistakably as pagination rather than a form row. */
const Stepper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 20px;
`;

const StepButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border-radius: 5px;
  border: 1px solid var(--border);
  background: transparent;
  color: var(--popover-foreground);
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  opacity: 0.8;
  transition:
    opacity 0.15s ease,
    border-color 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 1;
    border-color: var(--text-muted);
  }
  &:focus-visible {
    outline: 2px solid var(--text-muted);
    outline-offset: 1px;
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const StepCount = styled.span`
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: var(--popover-foreground);
  opacity: 0.75;
  min-width: 48px;
  text-align: center;
`;

const ScopeRow = styled.div`
  display: flex;
  gap: 6px;
`;

/* Bordered toggle. Selected is a raised GREY segment, not a gold fill: this
   panel is black-and-grey so the single green action reads as the action, and
   a gold slab here competed with it for the eye. Contrast carries the selected
   state — a lifted surface plus a lighter border and full-strength text
   against the muted inactive pair. */
const ScopeButton = styled.button`
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  line-height: 1.4;
  padding: 4px 8px;
  border-radius: var(--radius-input);
  border: 1px solid var(--border);
  background: transparent;
  color: var(--popover-foreground);
  opacity: 0.6;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not([data-selected]) {
    border-color: var(--text-muted);
    opacity: 0.85;
  }
  &[data-selected] {
    background: color-mix(in srgb, var(--popover-foreground) 14%, transparent);
    border-color: var(--text-muted);
    opacity: 1;
  }
`;

/* The source, stated in full once Advanced is open. */
const Preview = styled.div`
  font-size: 11px;
  line-height: 1.45;
  color: var(--popover-foreground);
  opacity: 0.75;
  background: var(--popover);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  padding: 6px 8px;
  max-height: 88px;
  overflow-y: auto;
  overflow-wrap: anywhere;
`;

/* Takes the slack in a header row so a trailing mark sits flush right. */
const HeaderLabel = styled(FieldLabel)`
  flex: 1;
`;

/* Header row for the Advanced disclosure: the toggle takes the label, the
   summary sits flush right. */
const SectionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SectionToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--popover-foreground);
  font-family: 'Inter', sans-serif;
  text-align: left;

  &:focus-visible {
    outline: 2px solid var(--text-muted);
    outline-offset: 2px;
  }
`;

/* Quiet scope/steer summary on the collapsed Advanced row. */
const ToggleSummary = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  color: var(--popover-foreground);
  opacity: 0.5;
`;

/* Label + info mark for the base-text scope inside Advanced. */
const BaseTextLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

/* Circular "i" beside a heading, carrying that section's tooltip. */
const InfoMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--popover-foreground) 28%, transparent);
  color: var(--popover);
  font-size: 9px;
  font-weight: var(--font-weight-medium);
  font-style: italic;
  line-height: 1;
  cursor: help;
`;

/* Wraps and caps its width rather than stretching into a single unreadable
   line — the base-text copy is a full sentence. */
const INFO_TOOLTIP_STYLE = {
  ...MODAL_TOOLTIP_STYLE,
  maxWidth: '260px',
  whiteSpace: 'normal' as const,
  lineHeight: 1.45,
};

/* Bordered-field primitive: border-color escalates on hover and focus, no
   outline halo — docs/design-system.md § Form Fields. */
const Instructions = styled.textarea`
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  line-height: 1.45;
  box-sizing: border-box;
  width: 100%;
  padding: 5px 8px;
  border-radius: var(--radius-input);
  border: 1px solid var(--border);
  background: var(--popover);
  color: var(--popover-foreground);
  resize: none;
  field-sizing: content;
  min-height: 26px;
  max-height: 70px;
  overflow-y: auto;
  outline: none;
  transition: border-color var(--transition-fast);

  &::placeholder {
    color: var(--text-muted);
  }
  &:hover {
    border-color: var(--text-muted);
  }
  &:focus-visible {
    border-color: var(--popover-foreground);
  }
`;

const ErrorText = styled.div`
  font-size: 11px;
  line-height: 1.4;
  color: var(--destructive);
`;

/* PLAT-10918 — amber "needs attention", not red: nothing failed and nothing is
   broken. The text is still exactly what the creator approved; it was simply
   written from a transcript that has since moved. Same warning treatment the
   translation modals use for a recoverable state. */
const StaleNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  line-height: 1.4;
  padding: 4px 8px;
  border-radius: var(--radius-input);
  color: var(--warning, #b58105);
  background: color-mix(in srgb, var(--warning, #b58105) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--warning, #b58105) 45%, transparent);
  cursor: help;
`;

/* The InfoMark's sibling: same circular glyph, warning register. */
const StaleMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  border-radius: 999px;
  background: var(--warning, #b58105);
  color: var(--popover);
  font-size: 9px;
  font-weight: var(--font-weight-medium);
  line-height: 1;
`;
